import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { childImageDisplaySettingsAPI } from '../utils/api';

const MIN_INTERVAL = 1;
const MAX_INTERVAL = 60;
const DEFAULT_INTERVAL = 5;

const ChildImageDisplaySettings: React.FC = () => {
  const { currentNursery } = useAuth();
  const { canAccessChildImageDisplaySettings, canEditChildImageDisplaySettings } = useRolePermissions();
  const { t } = useTranslation('pages', { keyPrefix: 'childImageDisplaySettings' });
  const { t: tc } = useTranslation('common');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [intervalValue, setIntervalValue] = useState(String(DEFAULT_INTERVAL));
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);

  const validateInterval = useCallback(() => {
    const parsed = Number(intervalValue);
    if (!Number.isInteger(parsed) || parsed < MIN_INTERVAL || parsed > MAX_INTERVAL) {
      return null;
    }
    return parsed;
  }, [intervalValue]);

  const fetchSettings = useCallback(async () => {
    if (!currentNursery) return;

    try {
      setLoading(true);
      setError(null);
      const response = await childImageDisplaySettingsAPI.getSettings(currentNursery.nursery_id);
      const settings = response.data;
      setIntervalValue(String(settings?.display_interval_minutes ?? DEFAULT_INTERVAL));
      setUpdatedAt(settings?.updated_at ?? null);
    } catch (err: any) {
      console.error('Error fetching child image display settings:', err);
      setError(err?.response?.data?.message || t('fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [currentNursery, t]);

  useEffect(() => {
    if (canAccessChildImageDisplaySettings) {
      fetchSettings();
    } else {
      setLoading(false);
    }
  }, [canAccessChildImageDisplaySettings, fetchSettings]);

  const handleSave = async () => {
    if (!currentNursery || !canEditChildImageDisplaySettings) return;

    const displayInterval = validateInterval();
    if (displayInterval === null) {
      setError(t('validationError'));
      setSuccess(null);
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const response = await childImageDisplaySettingsAPI.updateSettings(currentNursery.nursery_id, {
        display_interval_minutes: displayInterval,
      });
      setIntervalValue(String(response.data.display_interval_minutes));
      setUpdatedAt(response.data.updated_at);
      setSuccess(response.message || t('saveSuccess'));
    } catch (err: any) {
      console.error('Error saving child image display settings:', err);
      setError(err?.response?.data?.message || t('saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (!canAccessChildImageDisplaySettings) {
    return (
      <Layout title={t('title')}>
        <Container className="mt-4">
          <Alert variant="danger">{t('accessDenied')}</Alert>
        </Container>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout title={t('title')}>
        <Container className="py-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">{t('loading')}</p>
        </Container>
      </Layout>
    );
  }

  const validInterval = validateInterval();
  const formattedUpdatedAt = updatedAt ? new Date(updatedAt).toLocaleString() : tc('messages.na');

  return (
    <Layout title={t('title')}>
      <Container fluid className="px-3">
        <div className="mb-4">
          <h2 className="fw-bold text-primary">{t('title')}</h2>
          <p className="text-muted mb-0">{t('subtitle')}</p>
        </div>

        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}
        {!canEditChildImageDisplaySettings && (
          <Alert variant="info">{t('viewOnly')}</Alert>
        )}

        <Form>
          <Card className="mb-4 shadow-sm border-0">
            <Card.Header className="bg-white py-3 border-0">
              <h5 className="mb-0 fw-bold">{tc('labels.generalSettings')}</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">{t('intervalLabel')}</Form.Label>
                    <Form.Control
                      type="number"
                      min={MIN_INTERVAL}
                      max={MAX_INTERVAL}
                      value={intervalValue}
                      onChange={(event) => setIntervalValue(event.target.value)}
                      isInvalid={intervalValue !== '' && validInterval === null}
                      disabled={!canEditChildImageDisplaySettings || saving}
                    />
                    <Form.Control.Feedback type="invalid">
                      {t('validationError')}
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">{t('helperText')}</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <div className="bg-light rounded p-3 h-100">
                    <div className="small text-muted mb-1">{t('estimatedDensityLabel')}</div>
                    <div className="fw-semibold">
                      {validInterval ? t('estimatedDensity', { minutes: validInterval }) : t('estimatedDensityFallback')}
                    </div>
                    <div className="small text-muted mt-3">{t('updatedAt', { value: formattedUpdatedAt })}</div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <div className="d-flex justify-content-end mb-5">
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={saving || !canEditChildImageDisplaySettings}
              style={{ minWidth: '150px' }}
            >
              {saving ? <Spinner animation="border" size="sm" /> : t('saveSettings')}
            </Button>
          </div>
        </Form>
      </Container>
    </Layout>
  );
};

export default ChildImageDisplaySettings;
